import { CreditCard } from 'data/shared/__autogenerated__/types/CreditCard';
import { CreditCardData } from 'data/shared/CreditCardData';
import { useScreenSize } from '../hooks/useScreenSize';
import { CardData } from 'data/types/CardData';
import { CardRewardsTooltip } from './design-system/CardRewardsTooltip';
import { WalletActionButtons, CategoryEarnRatesData } from './WalletActionButtons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { CategoryEarnRatePill } from './CreditPills';
import { CreditCardRewardsType } from 'data/shared/CreditCardRewardsTypes';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { formatEarnRate } from '../data/shared/utils/formatEarnRate';
import { formatRewardsType } from '../data/shared/utils/formatRewardsType';
import { Tooltip } from './Tooltip';
import { useState, useEffect, useRef, useMemo } from 'react';
import { CreditCardImage } from './design-system/CreditCardImage';
import { shouldShowCategory } from 'data/shared/utils/categoryFilterUtils';

interface CategoryPill {
  category: string;
  earnRate: number;
  rewardsType: CreditCardRewardsType;
  cardName: string;
}

export const SelectedCardsIndicator = ({
  selectedCards,
  onCardClick,
  onRemoveAll,
  categoryPills = [],
  categoryEarnRates,
  defaultCategoryEarnRates,
  savedUrl,
  setSavedUrl,
}: {
  selectedCards: CreditCard[];
  onCardClick?: (card: CreditCard, walletIndex?: number) => void;
  onRemoveAll?: () => void;
  categoryPills?: CategoryPill[];
  categoryEarnRates?: CategoryEarnRatesData;
  defaultCategoryEarnRates?: CategoryEarnRatesData;
  savedUrl: string | null;
  setSavedUrl: (url: string | null) => void;
}) => {
  const screenSize = useScreenSize();
  const isMobile = screenSize === 'mobile';

  // Calculate total value for each card based on categories where it's the best card
  // This is used to sort cards by their contribution to the wallet
  const sortedCardData = useMemo(() => {
    // Build a map of card (by index) -> total value sum
    const cardValueMap = new Map<number, number>();

    if (categoryEarnRates) {
      Object.entries(categoryEarnRates).forEach(([category, data]) => {
        if (!data?.sortedCardsByEarnRate?.length) {
          return;
        }

        const bestCard = data.sortedCardsByEarnRate[0];
        if (!bestCard || bestCard.earnRate <= 1) {
          return;
        }

        // Apply the same filtering logic as the UI
        if (!shouldShowCategory(category, categoryEarnRates)) {
          return;
        }

        const cardIndex = bestCard.cardIndex ?? selectedCards.indexOf(bestCard.card);
        if (cardIndex >= 0) {
          const currentValue = cardValueMap.get(cardIndex) || 0;
          cardValueMap.set(cardIndex, currentValue + bestCard.totalValue);
        }
      });
    }

    // Create sorted list with original indices
    return selectedCards
      .map((cardId, index) => ({
        cardData: CreditCardData[cardId],
        originalIndex: index,
        totalValue: cardValueMap.get(index) || 0,
      }))
      .filter((item): item is { cardData: CardData; originalIndex: number; totalValue: number } =>
        item.cardData !== undefined
      )
      .sort((a, b) => {
        // Sort by total value (highest first), then by original index for stability
        if (b.totalValue !== a.totalValue) {
          return b.totalValue - a.totalValue;
        }
        return a.originalIndex - b.originalIndex;
      });
  }, [selectedCards, categoryEarnRates]);

  // Detect when the sticky container becomes "stuck"
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  const shouldRender = selectedCards.length > 0;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(!entry.isIntersecting);
      },
      // Shrink the top of the root by 70px to match sticky top offset
      { rootMargin: '-70px 0px 0px 0px' }
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [shouldRender]);

  if (selectedCards.length === 0) {
    return null;
  }

  return (
    <>
    {/* Sentinel — sits at the natural position; when it scrolls out, the container is stuck */}
    <div ref={sentinelRef} style={{ height: 1, marginBottom: -1 }} />
    <div
      style={{
        position: isMobile ? 'relative' : 'sticky',
        top: isMobile ? undefined : 70,
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        zIndex: 999,
        border: '1px solid #e0e0e0',
        transition: 'all 0.3s ease',
        maxWidth: '1200px',
        margin: '20px auto',
        width: isMobile ? 'auto' : 'fit-content',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 10 : 16,
          padding: isMobile ? '12px 14px' : '16px 24px',
        }}
      >
        <WalletActionButtons
          selectedCards={selectedCards}
          onRemoveAll={onRemoveAll}
          variant="desktop"
          savedUrl={savedUrl}
          setSavedUrl={setSavedUrl}
          categoryEarnRates={categoryEarnRates}
          defaultCategoryEarnRates={defaultCategoryEarnRates}
        >
          {({ linkArea, shareButton, redditButton }) => (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  overflowX: 'auto',
                  overflowY: 'visible',
                  scrollbarWidth: 'thin',
                  flex: isMobile ? '1 1 100%' : '1 1 0',
                  minWidth: 0,
                  position: 'relative',
                }}
              >
                {sortedCardData.map(({ cardData, originalIndex }) => (
                  <div
                    key={`${cardData.id}-${originalIndex}`}
                    style={{
                      width: 80,
                      height: 50,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CardRewardsTooltip card={cardData} position="auto">
                      <div
                        onClick={() => onCardClick?.(cardData.id, originalIndex)}
                        style={{
                          width: 80,
                          height: 50,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 6,
                          cursor: 'pointer',
                        }}
                      >
                        <CreditCardImage
                          imageName={cardData.imageName}
                          alt={cardData.name}
                          width={80}
                          height={50}
                          style={{
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                          }}
                        />
                      </div>
                    </CardRewardsTooltip>
                  </div>
                ))}
              </div>

              {linkArea}

              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {shareButton}
                {redditButton}
                <button
                  onClick={() => {
                    window.open(window.location.origin, '_blank');
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#e5e7eb',
                    color: '#1f2937',
                    fontWeight: 500,
                    fontSize: 14,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Start new
                </button>
              </div>
            </>
          )}
        </WalletActionButtons>

      </div>

      {!isMobile && categoryPills.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateRows: isStuck ? '0fr' : '1fr',
            transition: 'grid-template-rows 0.3s ease',
          }}
        >
        <div style={{ overflow: isStuck ? 'hidden' : 'visible' }}>
            <div
              style={{
                backgroundColor: '#EEF1F4',
                padding: isMobile ? '10px 14px' : '12px 24px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <p style={{ fontSize: '12px', fontWeight: 600, color: '#333', margin: 0 }}>
                Earn rate summary:
              </p>
              {categoryPills.map((pill, index) => (
                <Tooltip
                  key={`${pill.category}-${index}`}
                  content={`Earn ${formatEarnRate(pill.earnRate, pill.rewardsType)} ${formatRewardsType(pill.rewardsType)} on ${formatSpendCategory(pill.category)} with ${pill.cardName}`}
                  position="top"
                  maxWidth={260}
                >
                  <CategoryEarnRatePill
                    category={pill.category}
                    earnRate={pill.earnRate}
                    rewardsType={pill.rewardsType}
                    title={`Earn ${formatEarnRate(pill.earnRate, pill.rewardsType)} ${formatRewardsType(pill.rewardsType)} on ${formatSpendCategory(pill.category)} with ${pill.cardName}`}
                    style={{ backgroundColor: 'white' }}
                  />
                </Tooltip>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};
